import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, Lock, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/site/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cents) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

export default function PayPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const canceled = params.get("canceled") === "1";
  const [pr, setPr] = useState(null);
  const [error, setError] = useState("");
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payment-requests/${token}`)
      .then((r) => setPr(r.data))
      .catch(() => setError("This payment link is invalid or no longer available."));
  }, [token]);

  const pay = async () => {
    setRedirecting(true);
    try {
      const { data } = await axios.post(`${API}/payment-requests/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start the payment. Please try again.");
      setRedirecting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFC] flex flex-col items-center justify-center px-6 py-12">
      <Logo />
      <div className="mt-8 w-full max-w-md rounded-3xl border border-navy-100 bg-white shadow-xl p-8" data-testid="pay-card">
        {error ? (
          <p className="text-center text-charcoal-700" role="alert" data-testid="pay-error">{error}</p>
        ) : !pr ? (
          <div className="flex items-center justify-center py-12 text-charcoal-700/60">
            <Loader2 size={22} className="animate-spin mr-2" aria-hidden /> Loading…
          </div>
        ) : pr.status === "paid" ? (
          <div className="text-center py-6" data-testid="pay-already-paid">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 size={30} aria-hidden />
            </span>
            <p className="mt-4 font-display text-xl font-semibold text-navy-900">Already paid — thank you!</p>
            <p className="mt-2 text-sm text-charcoal-700/80">This payment has been completed. No further action is needed.</p>
          </div>
        ) : (
          <>
            <p className="text-xs font-bold uppercase tracking-wide text-gold-600">{pr.stage_label}</p>
            <h1 className="mt-1.5 font-display text-2xl font-semibold text-navy-900">{pr.church_name}</h1>
            <p className="mt-1 text-sm text-charcoal-700/70">{pr.package_label}</p>
            {canceled && (
              <p className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="pay-canceled-note">
                Payment was canceled — you can try again whenever you're ready.
              </p>
            )}
            <div className="mt-6 rounded-2xl bg-[#FAFAFC] border border-navy-100 p-5 flex items-baseline justify-between">
              <span className="text-sm font-semibold text-charcoal-700">Amount due</span>
              <span className="font-display text-3xl font-bold text-navy-900" data-testid="pay-amount">{fmt(pr.amount)}</span>
            </div>
            <p className="mt-2 text-xs text-charcoal-700/60">Plus applicable tax, calculated at checkout.</p>
            <button
              onClick={pay}
              disabled={redirecting}
              data-testid="pay-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 text-navy-900 font-semibold px-8 py-4 text-lg hover:bg-gold-400 transition-colors disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2"
            >
              {redirecting ? <><Loader2 size={20} className="animate-spin" aria-hidden /> Redirecting…</> : <><Lock size={18} aria-hidden /> Pay securely with Stripe</>}
            </button>
            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-charcoal-700/60">
              <ShieldCheck size={14} className="text-gold-600" aria-hidden /> Secure payment powered by Stripe. Konek Church never sees your card details.
            </p>
          </>
        )}
      </div>
      <p className="mt-6 text-xs text-charcoal-700/50">Questions? Write to hello@konekchurch.com</p>
    </div>
  );
}
